import { getActivePreviewCompany, PREVIEW_AUTH_USER_ID } from './flag';
import { previewData, previewPartyRpc } from './data';
import { previewExecuteRefund, previewRefundRpc } from './refundFixture';
import { previewRecoveryPlanRpc } from './recoveryPlanFixture';
import { previewCommunicationRpc } from './communicationFixture';
import { previewTaskRpc } from './taskFixture';
import { previewNotebookRpc } from './notebookFixture';
import { matchesSearch } from '../../ui/foundation/search';

// Minimal Supabase-shaped mock client for Preview Mode. Implements exactly the surface the
// app uses (auth session, PostgREST-style .from() builder, .schema('app').rpc()). Backed by
// the deterministic in-memory datasets; no network, no real writes. Pagination cursors are
// never exercised (datasets < page size), so `.or()` is a safe no-op.
type Row = Record<string, unknown>;
type Result<T> = { data: T; error: null | { message: string } };

function str(v: unknown): string {
  if (v === null || v === undefined || typeof v === 'object') return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

class MockQuery implements PromiseLike<Result<Row[]>> {
  private readonly filters: ((r: Row) => boolean)[] = [];
  private readonly orderings: { col: string; asc: boolean }[] = [];
  private limitN = Infinity;

  constructor(private readonly table: string) {}

  private rows(): Row[] {
    return [...(previewData(getActivePreviewCompany()).tables[this.table] ?? [])];
  }

  select(): this {
    return this;
  }
  eq(col: string, val: unknown): this {
    this.filters.push((r) => str(r[col]) === str(val));
    return this;
  }
  in(col: string, vals: readonly unknown[]): this {
    const wanted = new Set(vals.map(str));
    this.filters.push((r) => wanted.has(str(r[col])));
    return this;
  }
  ilike(col: string, pattern: string): this {
    const needle = pattern.replace(/%/g, '').toLowerCase();
    this.filters.push((r) => str(r[col]).toLowerCase().includes(needle));
    return this;
  }
  gte(col: string, val: unknown): this {
    this.filters.push((r) => str(r[col]) >= str(val));
    return this;
  }
  lte(col: string, val: unknown): this {
    this.filters.push((r) => str(r[col]) <= str(val));
    return this;
  }
  not(col: string, _op: string, _val: unknown): this {
    this.filters.push((r) => r[col] !== null && r[col] !== undefined);
    return this;
  }
  or(expr?: string): this {
    // Keyset-cursor or-groups (gt/lt/eq shapes) stay a no-op — datasets are smaller than a
    // page. The SEARCH or-group (`col.ilike.*term*,col2.ilike.*term*`, P0-G C3) is honoured:
    // when membersApi moved from .ilike() to .or(...), a no-op here silently stopped the
    // members search from filtering at all in Preview Mode.
    if (typeof expr === 'string' && expr.includes('.ilike.')) {
      const alternatives = expr.split(',').flatMap((part) => {
        const m = /^(\w+)\.ilike\.\*?(.*?)\*?$/.exec(part.trim());
        return m === null ? [] : [{ col: m[1]!, needle: m[2]!.toLowerCase() }];
      });
      if (alternatives.length > 0) {
        this.filters.push((r) =>
          alternatives.some((a) => str(r[a.col]).toLowerCase().includes(a.needle)),
        );
      }
    }
    return this;
  }
  order(col: string, opts?: { ascending?: boolean }): this {
    this.orderings.push({ col, asc: opts?.ascending !== false });
    return this;
  }
  limit(n: number): this {
    this.limitN = n;
    return this;
  }

  private run(): Row[] {
    let rows = this.rows().filter((r) => this.filters.every((f) => f(r)));
    for (const o of [...this.orderings].reverse()) {
      rows = rows.sort((a, b) => {
        const cmp = str(a[o.col]).localeCompare(str(b[o.col]));
        return o.asc ? cmp : -cmp;
      });
    }
    return rows.slice(0, this.limitN);
  }

  maybeSingle<T = unknown>(): PromiseLike<Result<T | null>> {
    const first = this.run()[0] ?? null;
    return Promise.resolve({ data: first as T | null, error: null });
  }

  then<TResult1 = Result<Row[]>, TResult2 = never>(
    onfulfilled?: ((value: Result<Row[]>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return Promise.resolve<Result<Row[]>>({ data: this.run(), error: null }).then(onfulfilled, onrejected);
  }
}

interface RpcArgs {
  p_company?: string;
  p_payload?: Row;
  p_task_id?: string;
  p_patch?: Row;
  p_expected_version?: number;
  p_idempotency_key?: string;
  p_member?: string;
  p_status?: string;
  p_role_codes?: string[];
}

// Admin mutation RPCs (fn_admin_update_company/settings, 0045) are the only in-memory *write*
// path beyond fn_commission_transition_batch — sufficient version/idempotency/validation
// emulation for Slice 5A UI verification, not a re-implementation of the server contract.
const adminOps = new Map<string, { hash: string; result: unknown }>();

function adminInvalidPayload(): Result<unknown> {
  return { data: null, error: { message: 'ADMIN_INVALID_PAYLOAD' } };
}
function adminStaleWrite(): Result<unknown> {
  return { data: null, error: { message: 'ADMIN_STALE_WRITE' } };
}
function adminIdempotencyConflict(): Result<unknown> {
  return { data: null, error: { message: 'IDEMPOTENCY_CONFLICT' } };
}

function adminScopeForbidden(): Result<unknown> {
  return { data: null, error: { message: 'ADMIN_SCOPE_FORBIDDEN' } };
}
function adminLastAdmin(): Result<unknown> {
  return { data: null, error: { message: 'ADMIN_LAST_ADMIN' } };
}

function replay(opKey: string, hash: string): Result<unknown> | null {
  const existing = adminOps.get(opKey);
  if (existing === undefined) return null;
  return existing.hash === hash ? { data: existing.result, error: null } : adminIdempotencyConflict();
}

function findMemberRow(companyId: string, memberId: string): Row | undefined {
  return (previewData(companyId).tables['users'] ?? []).find((r) => r['id'] === memberId);
}
function otherActiveAdminCount(companyId: string, excludeId: string): number {
  return (previewData(companyId).tables['users'] ?? []).filter((r) => {
    if (r['id'] === excludeId) return false;
    if (r['membership_status'] !== 'active') return false;
    const roles = (r['user_roles'] as { roles: { code: string } }[] | undefined) ?? [];
    return roles.some((ur) => ur.roles.code === 'administrator');
  }).length;
}

const MEMBER_STATUS_VALUES = ['invited', 'active', 'inactive'];
function updateMemberStatusMock(companyId: string, args: RpcArgs): Result<unknown> {
  const { p_member: memberId, p_status: status, p_expected_version: expectedVersion, p_idempotency_key: key } = args;
  if (memberId === undefined || status === undefined || expectedVersion === undefined) return adminInvalidPayload();
  if (!MEMBER_STATUS_VALUES.includes(status)) return adminInvalidPayload();
  if (key === undefined || key.trim() === '') return adminInvalidPayload();

  const opKey = `${companyId}:set_member_status:${key}`;
  const hash = JSON.stringify({ memberId, status, expectedVersion });
  const replayed = replay(opKey, hash);
  if (replayed !== null) return replayed;

  const row = findMemberRow(companyId, memberId);
  if (row === undefined) return adminScopeForbidden();
  if (row['version'] !== expectedVersion) return adminStaleWrite();

  const current = row['membership_status'] as string;
  // Mirrors the server transition matrix (0043/0050): invited->active, inactive->active,
  // active->inactive, and invited->inactive (part 3B: cancel a pending invitation).
  const validTransition =
    (current === 'invited' && status === 'active') ||
    (current === 'inactive' && status === 'active') ||
    (current === 'active' && status === 'inactive') ||
    (current === 'invited' && status === 'inactive');
  if (!validTransition) return adminInvalidPayload();

  // Narrowed to active->inactive only (0050 correction): cancelling an invited membership never
  // evaluates the last-active-administrator count — an invitation that was never active cannot be
  // "the last active administrator".
  if (current === 'active' && status === 'inactive') {
    const roles = (row['user_roles'] as { roles: { code: string } }[] | undefined) ?? [];
    const isAdmin = roles.some((ur) => ur.roles.code === 'administrator');
    if (isAdmin && otherActiveAdminCount(companyId, memberId) === 0) return adminLastAdmin();
  }

  row['membership_status'] = status;
  row['version'] = (row['version']) + 1;
  const now = new Date().toISOString();
  if (status === 'active') row['activated_at'] = now;
  if (status === 'inactive') row['deactivated_at'] = now;
  const result = { member_id: memberId, status: row['membership_status'], version: row['version'] };
  adminOps.set(opKey, { hash, result });
  return { data: result, error: null };
}

const ROLE_CODE_VALUES = ['administrator', 'finance_manager', 'finance_operator', 'management', 'sales_manager'];
function updateMemberRolesMock(companyId: string, args: RpcArgs): Result<unknown> {
  const { p_member: memberId, p_role_codes: roleCodes, p_expected_version: expectedVersion, p_idempotency_key: key } = args;
  if (memberId === undefined || roleCodes === undefined || expectedVersion === undefined) return adminInvalidPayload();
  if (!Array.isArray(roleCodes) || roleCodes.some((c) => !ROLE_CODE_VALUES.includes(c))) return adminInvalidPayload();
  if (key === undefined || key.trim() === '') return adminInvalidPayload();

  const normalized = Array.from(new Set(roleCodes)).sort();
  const opKey = `${companyId}:set_member_roles:${key}`;
  const hash = JSON.stringify({ memberId, normalized, expectedVersion });
  const replayed = replay(opKey, hash);
  if (replayed !== null) return replayed;

  const row = findMemberRow(companyId, memberId);
  if (row === undefined) return adminScopeForbidden();
  if (row['version'] !== expectedVersion) return adminStaleWrite();

  const roles = (row['user_roles'] as { roles: { code: string } }[] | undefined) ?? [];
  const oldCodes = roles.map((ur) => ur.roles.code).sort();
  if (JSON.stringify(oldCodes) === JSON.stringify(normalized)) return adminInvalidPayload(); // same-set no-op

  if (row['membership_status'] === 'active' && oldCodes.includes('administrator') && !normalized.includes('administrator')) {
    if (otherActiveAdminCount(companyId, memberId) === 0) return adminLastAdmin();
  }

  row['user_roles'] = normalized.map((code) => ({ roles: { code } }));
  row['version'] = (row['version']) + 1;
  const result = { member_id: memberId, role_codes: normalized, version: row['version'] };
  adminOps.set(opKey, { hash, result });
  return { data: result, error: null };
}

// ── admin-invite Edge Function emulation (Slice 5B; P0-G identity+role) ───────────────────────
// Mirrors src/admin/admin-invite-handler.ts's observable contract (email/first_name/last_name/
// role_code/idempotency_key in, generic {ok,status} out) closely enough for UI verification —
// not a reimplementation of Auth Admin/service-role logic, no real email sent.
const inviteOps = new Map<string, { hash: string; result: unknown }>();
const invitedEmailIndex = new Map<string, string>();
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function inviteMemberMock(companyId: string, body: Row): Result<unknown> {
  const email = typeof body['email'] === 'string' ? body['email'].trim().toLowerCase() : '';
  const key = typeof body['idempotency_key'] === 'string' ? body['idempotency_key'] : '';
  const firstName = typeof body['first_name'] === 'string' ? body['first_name'].trim() : '';
  const lastName = typeof body['last_name'] === 'string' ? body['last_name'].trim() : '';
  const roleCode = typeof body['role_code'] === 'string' ? body['role_code'] : '';
  if (!EMAIL_RE.test(email)) return { data: null, error: { message: 'INVALID_EMAIL' } };
  if (firstName === '' || lastName === '') return { data: null, error: { message: 'INVALID_NAME' } };
  if (roleCode === '') return { data: null, error: { message: 'INVALID_ROLE' } };
  if (key.trim() === '') return { data: null, error: { message: 'INVALID_IDEMPOTENCY_KEY' } };

  const opKey = `${companyId}:invite:${key}`;
  const existing = inviteOps.get(opKey);
  if (existing !== undefined) {
    return existing.hash === email ? { data: existing.result, error: null } : { data: null, error: { message: 'IDEMPOTENCY_CONFLICT' } };
  }

  const emailKey = `${companyId}:${email}`;
  let status: 'invited' | 'already_member';
  if (invitedEmailIndex.has(emailKey)) {
    status = 'already_member';
  } else {
    const memberId = `mb-invited-${invitedEmailIndex.size + 1}-${companyId.slice(0, 4)}`;
    invitedEmailIndex.set(emailKey, memberId);
    const now = new Date().toISOString();
    (previewData(companyId).tables['users'] as Row[] | undefined)?.push({
      id: memberId,
      company_id: companyId,
      auth_user_id: `au-invited-${invitedEmailIndex.size}`,
      display_name: `${firstName} ${lastName}`,
      email,
      membership_status: 'invited',
      version: 1,
      invited_at: now,
      activated_at: null,
      deactivated_at: null,
      created_at: now,
      user_roles: [{ roles: { code: roleCode } }],
    });
    status = 'invited';
  }
  // Enumeration-safe: identical response shape regardless of new-vs-existing (§5 [M2]).
  const result = { ok: true, status };
  inviteOps.set(opKey, { hash: email, result });
  return { data: result, error: null };
}

// ── admin-invite resend emulation (part 3A) ───────────────────────────────────────────────────
// Mirrors the observable contract of the real resend orchestration (part 2C) closely enough for
// UI verification: exact { action:"resend", member_id, idempotency_key } body, invited-only +
// same-company target resolution, uniform generic { ok:true, status:"processed" } response. Does
// NOT emulate email delivery, leases, or Auth identity state — Preview never sends anything and
// never mutates membership_status/version/roles/timestamps on resend (cancel/activate are the
// only mutating member operations; resend is observationally a no-op on the member row itself,
// same as the real handler's confirmed-user/cooldown/processing paths).
const RESEND_ALLOWED_BODY_KEYS = new Set(['action', 'member_id', 'idempotency_key']);

function resendInvitationMock(companyId: string, body: Row): Result<unknown> {
  if (!Object.keys(body).every((k) => RESEND_ALLOWED_BODY_KEYS.has(k))) {
    return { data: null, error: { message: 'INVALID_REQUEST' } };
  }
  const memberId = typeof body['member_id'] === 'string' ? body['member_id'] : '';
  const key = typeof body['idempotency_key'] === 'string' ? body['idempotency_key'] : '';
  if (memberId.trim() === '') return { data: null, error: { message: 'INVALID_MEMBER_ID' } };
  if (key.trim() === '') return { data: null, error: { message: 'INVALID_IDEMPOTENCY_KEY' } };

  // Target resolution scoped strictly to THIS company's dataset — a cross-company id is simply
  // not found here (no disclosure), exactly like findMemberRow's other admin-mutation callers.
  const row = findMemberRow(companyId, memberId);
  if (row === undefined || row['membership_status'] !== 'invited') {
    return { data: null, error: { message: 'ADMIN_SCOPE_FORBIDDEN' } };
  }

  // No mutation of the member row: no status/version/role/timestamp change, no fabricated
  // email/address data — the row is read-only in this path.
  return { data: { ok: true, status: 'processed' }, error: null };
}

function updateCompanyMock(companyId: string, args: RpcArgs): Result<unknown> {
  const patch = args.p_patch;
  if (patch === undefined || patch === null || typeof patch !== 'object' || Array.isArray(patch)) return adminInvalidPayload();
  const keys = Object.keys(patch);
  if (keys.some((k) => k !== 'name') || !('name' in patch)) return adminInvalidPayload();
  const name = patch['name'];
  if (typeof name !== 'string' || name.trim() === '') return adminInvalidPayload();

  const key = args.p_idempotency_key;
  if (key === undefined || key.trim() === '') return adminInvalidPayload();
  const opKey = `${companyId}:update_company:${key}`;
  const hash = JSON.stringify({ name: name.trim() });
  const replayed = replay(opKey, hash);
  if (replayed !== null) return replayed;

  const row = previewData(companyId).tables['companies']?.[0] as Row | undefined;
  if (row === undefined) return { data: null, error: { message: 'ADMIN_SCOPE_FORBIDDEN' } };
  if (row['version'] !== args.p_expected_version) return adminStaleWrite();
  if (row['name'] === name.trim()) return adminInvalidPayload(); // same-value no-op

  row['name'] = name.trim();
  row['version'] = (row['version'] as number) + 1;
  const result = { company_id: companyId, name: row['name'], version: row['version'] };
  adminOps.set(opKey, { hash, result });
  return { data: result, error: null };
}

const LIMIT_KEYS = ['maxCompressedBytes', 'maxDecompressedBytes', 'maxRows', 'maxSheets', 'maxCellTextLength'];

function updateSettingsMock(companyId: string, args: RpcArgs): Result<unknown> {
  const patch = args.p_patch;
  if (patch === undefined || patch === null || typeof patch !== 'object' || Array.isArray(patch)) return adminInvalidPayload();
  const allowed = ['default_payment_terms_days', 'security_limits', 'system_cutover_date', 'opening_balance_as_of_date'];
  const keys = Object.keys(patch);
  if (keys.some((k) => !allowed.includes(k)) || keys.length === 0) return adminInvalidPayload();

  if ('default_payment_terms_days' in patch) {
    const v = patch['default_payment_terms_days'];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0) return adminInvalidPayload();
  }
  if ('security_limits' in patch) {
    const limits = patch['security_limits'];
    if (typeof limits !== 'object' || limits === null || Array.isArray(limits)) return adminInvalidPayload();
    const lk = Object.keys(limits);
    if (lk.length !== LIMIT_KEYS.length || !LIMIT_KEYS.every((k) => k in limits)) return adminInvalidPayload();
    for (const k of LIMIT_KEYS) {
      const v = (limits as Row)[k];
      if (typeof v !== 'number' || !Number.isInteger(v) || v <= 0) return adminInvalidPayload();
    }
  }

  const key = args.p_idempotency_key;
  if (key === undefined || key.trim() === '') return adminInvalidPayload();
  const opKey = `${companyId}:update_settings:${key}`;
  const hash = JSON.stringify(patch);
  const replayed = replay(opKey, hash);
  if (replayed !== null) return replayed;

  const row = previewData(companyId).tables['company_settings']?.[0] as Row | undefined;
  if (row === undefined) return { data: null, error: { message: 'ADMIN_SCOPE_FORBIDDEN' } };
  if (row['version'] !== args.p_expected_version) return adminStaleWrite();

  const nextTerms = 'default_payment_terms_days' in patch ? patch['default_payment_terms_days'] : row['default_payment_terms_days'];
  const nextLimits = 'security_limits' in patch ? patch['security_limits'] : row['security_limits'];
  const nextCutover = 'system_cutover_date' in patch ? patch['system_cutover_date'] : row['system_cutover_date'];
  const nextOpening = 'opening_balance_as_of_date' in patch ? patch['opening_balance_as_of_date'] : row['opening_balance_as_of_date'];
  const noop =
    nextTerms === row['default_payment_terms_days'] &&
    JSON.stringify(nextLimits) === JSON.stringify(row['security_limits']) &&
    nextCutover === row['system_cutover_date'] &&
    nextOpening === row['opening_balance_as_of_date'];
  if (noop) return adminInvalidPayload();

  row['default_payment_terms_days'] = nextTerms;
  row['security_limits'] = nextLimits;
  row['system_cutover_date'] = nextCutover ?? null;
  row['opening_balance_as_of_date'] = nextOpening ?? null;
  row['version'] = (row['version'] as number) + 1;
  const result = {
    company_id: companyId,
    default_payment_terms_days: row['default_payment_terms_days'],
    security_limits: row['security_limits'],
    system_cutover_date: row['system_cutover_date'],
    opening_balance_as_of_date: row['opening_balance_as_of_date'],
    version: row['version'],
  };
  adminOps.set(opKey, { hash, result });
  return { data: result, error: null };
}

function rpc(fn: string, args: RpcArgs = {}): Promise<Result<unknown>> {
  const companyId = args.p_company ?? getActivePreviewCompany();
  const cd = previewData(companyId);
  if (fn === 'fn_alert_task_acknowledge' || fn === 'fn_alert_task_done' || fn === 'fn_alert_task_dismiss') {
    const action = fn === 'fn_alert_task_acknowledge' ? 'acknowledge' : fn === 'fn_alert_task_done' ? 'done' : 'dismiss';
    return Promise.resolve({ data: cd.alertAction(str(args.p_task_id), action), error: null });
  }
  if (fn === 'fn_commission_transition_batch') {
    const payload = args.p_payload ?? {};
    cd.transition(str(payload['batchId']), str(payload['to']));
    return Promise.resolve({ data: {}, error: null });
  }
  if (fn === 'fn_admin_update_company') {
    return Promise.resolve(updateCompanyMock(companyId, args));
  }
  if (fn === 'fn_admin_update_settings') {
    return Promise.resolve(updateSettingsMock(companyId, args));
  }
  if (fn === 'fn_admin_set_member_status') {
    return Promise.resolve(updateMemberStatusMock(companyId, args));
  }
  if (fn === 'fn_admin_set_member_roles') {
    return Promise.resolve(updateMemberRolesMock(companyId, args));
  }
  const party = previewPartyRpc(fn, args as Record<string, unknown>);
  if (party !== undefined) return Promise.resolve({ data: party, error: null });
  // D-210 — the two refund read RPCs (0139).
  const refund = previewRefundRpc(fn, args as Record<string, unknown>);
  if (refund !== undefined) return Promise.resolve(refund);
  // D-212 — the recovery-plan panel's two p_plan_id read RPCs.
  const plan = previewRecoveryPlanRpc(fn, args as Record<string, unknown>);
  if (plan !== undefined) return Promise.resolve(plan);
  // D-216 — the communication journal's list/create RPCs (0142).
  const communication = previewCommunicationRpc(fn, args as Record<string, unknown>);
  if (communication !== undefined) return Promise.resolve(communication);
  // D-219 — internal tasks' RPCs (0145).
  const internalTask = previewTaskRpc(fn, args as Record<string, unknown>);
  if (internalTask !== undefined) return Promise.resolve(internalTask);
  // D-223 — the Management Notebook's RPCs (0146/0147).
  const notebook = previewNotebookRpc(fn, args as Record<string, unknown>);
  if (notebook !== undefined) return Promise.resolve(notebook);
  const handler = cd.rpc[fn];
  if (handler === undefined) {
    return Promise.resolve({ data: null, error: { message: `PREVIEW_RPC_NOT_IMPLEMENTED:${fn}` } });
  }
  const payload = args.p_payload ?? {};
  return Promise.resolve({ data: applyPreviewSearch(fn, payload, handler(payload), cd.tables['customers'] ?? []), error: null });
}

// D-213 — Preview Mode approximation of 0141's `filters.search`: the canned report rows are
// narrowed with the SAME matcher the app uses (name or tax id; manager name on the manager
// summary). Rows only — canned totals are left as they are; this is UI verification, not a
// re-implementation of the report.
const SEARCHABLE_REPORTS = new Set([
  'fn_report_aging', 'fn_report_payments', 'fn_report_advances', 'fn_report_exceptions',
  'fn_report_manager_summary', 'fn_alert_task_list',
  'fn_report_debtor_summary', // D-233 (0150)
]);
function applyPreviewSearch(fn: string, payload: Row, data: unknown, customers: readonly Row[]): unknown {
  const filters = (payload['filters'] ?? {}) as Row;
  const q = str(filters['search']).trim();
  if (q === '' || !SEARCHABLE_REPORTS.has(fn) || data === null || typeof data !== 'object') return data;
  const taxOf = (id: unknown): string => str(customers.find((c) => c['id'] === id)?.['tax_id']);
  const keep = (r: Row): boolean => {
    if (fn === 'fn_report_manager_summary') {
      const m = (r['manager'] ?? {}) as Row;
      return m['userId'] == null
        ? matchesSearch(q, 'Unassigned', 'არ არის მინიჭებული')
        : matchesSearch(q, str(m['displayName']));
    }
    return matchesSearch(q, str(r['customerName']), taxOf(r['customerId']));
  };
  const d = data as Row;
  const key = fn === 'fn_alert_task_list' ? 'items' : 'rows';
  const list = d[key];
  return Array.isArray(list) ? { ...d, [key]: (list as Row[]).filter(keep) } : data;
}

const MOCK_SESSION = {
  user: { id: PREVIEW_AUTH_USER_ID, email: 'preview@debtor.local', app_metadata: {}, user_metadata: {} },
};

// Manual-QA-only hook (M2): a deterministic way to simulate a concurrent writer bumping
// companies/company_settings.version, so ADMIN_STALE_WRITE can be exercised by hand from the
// browser console (`__previewAdmin.bumpCompanyVersion()`) without a visible in-product control
// and without any page-level Preview branching. Only ever attached when the mock client itself
// is constructed (i.e. only in Preview Mode); never present in a production build's runtime
// path. The Preview conflict this produces flows through the exact same fn_admin_update_company/
// settings mock handlers above, so the UI exercises identical handling to production.
function bumpVersion(table: 'companies' | 'company_settings', companyId?: string): number | null {
  const row = previewData(companyId ?? getActivePreviewCompany()).tables[table]?.[0] as Row | undefined;
  if (row === undefined) return null;
  row['version'] = (row['version'] as number) + 1;
  return row['version'] as number;
}
// Slice 5B: same manual-QA pattern for a specific member row (fn_admin_set_member_status/roles
// stale-write cases can't be triggered any other way without a second real session).
function bumpMemberVersion(memberId: string, companyId?: string): number | null {
  const row = findMemberRow(companyId ?? getActivePreviewCompany(), memberId);
  if (row === undefined) return null;
  row['version'] = (row['version'] as number) + 1;
  return row['version'] as number;
}

export function createMockClient(): unknown {
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>)['__previewAdmin'] = {
      bumpCompanyVersion: (companyId?: string) => bumpVersion('companies', companyId),
      bumpSettingsVersion: (companyId?: string) => bumpVersion('company_settings', companyId),
      bumpMemberVersion: (memberId: string, companyId?: string) => bumpMemberVersion(memberId, companyId),
    };
  }
  return {
    auth: {
      getSession: () => Promise.resolve({ data: { session: MOCK_SESSION }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: (): void => {} } } }),
      signOut: () => Promise.resolve({ error: null }),
      signInWithPassword: () => Promise.resolve({ data: { session: MOCK_SESSION }, error: null }),
      // Slice P0-E — the password-setup screen (reachable in Preview via a ?type=invite landing)
      // calls this. Without it the call throws and the screen strands the user on a spinner.
      updateUser: () => Promise.resolve({ data: { user: MOCK_SESSION.user }, error: null }),
    },
    from: (table: string): MockQuery => new MockQuery(table),
    schema: (): { rpc: (fn: string, args: RpcArgs) => Promise<Result<unknown>> } => ({ rpc }),
    functions: {
      // M2 correction: Preview must fail exactly like the real admin-invite handler when the
      // caller omits x-company-id (400 ADMIN_SCOPE_FORBIDDEN, admin-invite-handler.ts:167-170) —
      // it must never silently substitute the active Preview company for a missing header. No
      // fallback to getActivePreviewCompany() here.
      invoke: (fn: string, opts: { body?: Row; headers?: Record<string, string> }): Promise<Result<unknown>> => {
        const companyId = opts.headers?.['x-company-id'];
        if (companyId === undefined || companyId.trim() === '') {
          return Promise.resolve({ data: null, error: { message: 'ADMIN_SCOPE_FORBIDDEN' } });
        }
        if (fn === 'admin-invite') {
          const body = opts.body ?? {};
          if (body['action'] === 'resend') {
            return Promise.resolve(resendInvitationMock(companyId, body));
          }
          if (body['action'] === 'execute_refund') {
            return Promise.resolve(previewExecuteRefund(companyId, body));
          }
          return Promise.resolve(inviteMemberMock(companyId, body));
        }
        return Promise.resolve({ data: null, error: { message: `PREVIEW_FUNCTION_NOT_IMPLEMENTED:${fn}` } });
      },
    },
  };
}
